import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  RitualBathFormChatSummaryCard,
  type FormChatEditableFieldOption,
  type FormChatFieldInput,
  type FormChatListOption,
  type RitualBathChatSummary,
} from "gsintegrations";
import { trackRitualBathsError } from "../analytics/ritualBaths";
import type {
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
  RitualBathReferenceData,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { useBathCategoriesQuery } from "./useBathCategoryHooks";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useFormChatSubmitRitualBath } from "./useFormChatSubmitRitualBath";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";

export const RITUAL_BATH_LIST_EDIT_REPLIES = [
  "Adicionar mais",
  "Remover último",
  "Refazer lista",
];

const LIST_FIELDS = new Set(["bathIngredients", "bathSteps"]);

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Ativo",
  DRAFT: "Rascunho",
  INACTIVE: "Inativo",
};

const EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "bathCategoryId", label: "Categoria" },
  { key: "bathTitle", label: "Nome" },
  { key: "bathDescription", label: "Descrição" },
  { key: "bathIntroduction", label: "Introdução" },
  { key: "bathSource", label: "Fonte" },
  { key: "bathImage", label: "Foto" },
  { key: "bathIngredients", label: "Ingredientes" },
  { key: "bathSteps", label: "Modo de preparo" },
  { key: "bathStatus", label: "Status" },
  { key: "scheduleOnCalendar", label: "Agendar no calendário" },
  { key: "scheduleDate", label: "Data do agendamento" },
  { key: "scheduleStartTime", label: "Horário de início" },
  { key: "scheduleEndTime", label: "Horário de término" },
];

const SCHEDULE_DETAIL_FIELDS = new Set([
  "scheduleDate",
  "scheduleStartTime",
  "scheduleEndTime",
]);

const formatChatDate = (value: string | undefined) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

const countLabel = (count: number, singular: string, plural: string) =>
  count === 0 ? "Nenhum" : `${count} ${count === 1 ? singular : plural}`;

interface UseRitualBathFormChatOptions {
  enabled: boolean;
  editTargetId?: string;
  confirmLabel: string;
  sessionId?: string;
  formState: FormChatState;
  dirtyFields: string[];
  summary?: RitualBathChatSummary;
  onResponse: (response: FormChatMessageResponse) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
}

export function useRitualBathFormChatHelpers({
  enabled,
  editTargetId,
  confirmLabel,
  sessionId,
  formState,
  dirtyFields,
  summary,
  onResponse,
  onError,
  onReset,
  onEdit,
}: UseRitualBathFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isEdit = Boolean(editTargetId);
  const { data: categories } = useBathCategoriesQuery(enabled, terreiroId);
  const { submitRitualBathByChat, canCreate, isLimitLoading, limitMessage, isSubmitting } =
    useFormChatSubmitRitualBath({ editTargetId, enabled });

  const [image, setImage] = useState<File | null>(null);
  const imagePreviewUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : null),
    [image],
  );
  useEffect(
    () => () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    },
    [imagePreviewUrl],
  );
  useEffect(() => {
    if (formState.bathImage !== "attached") setImage(null);
  }, [formState.bathImage]);

  const categoryOptions = useMemo<FormChatListOption[]>(
    () =>
      (categories ?? []).map((category) => ({
        id: String(category.id),
        label: category.name,
      })),
    [categories],
  );

  const ritualBathReferenceData = useMemo<RitualBathReferenceData>(
    () => ({
      bathCategories: categoryOptions.map((option) => ({
        id: option.id,
        name: option.label,
      })),
    }),
    [categoryOptions],
  );

  const { mutateAsync: sendRitualBathMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "ritual-bath-message"],
    mutationFn: async (
      payload: FormChatMessageRequest,
    ): Promise<FormChatMessageResponse> => {
      if (terreiroId) return formChatRepository.sendMessage(terreiroId, payload);
      if (mediumId) return formChatRepository.sendPersonalMessage(mediumId, payload);
      throw new Error("Owner scope required");
    },
    onError: (error) => {
      trackRitualBathsError(
        isEdit ? "ritual_bath_update_error" : "ritual_bath_create_error",
        { ...productBase, trigger: "mutation" },
        error,
        { source: "form_chat", stage: "chat_message" },
      );
    },
  });

  const sendRitualBathRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId && !mediumId) return undefined;
      try {
        const response = await sendRitualBathMutation({
          entityType: "ritual_bath",
          referenceData: ritualBathReferenceData,
          ...(editTargetId
            ? { mode: "edit" as const, targetEntityId: editTargetId }
            : {}),
          ...payload,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        onError?.(extractAxiosApiError(err));
        throw err;
      }
    },
    [
      editTargetId,
      mediumId,
      onError,
      onResponse,
      ritualBathReferenceData,
      sendRitualBathMutation,
      terreiroId,
    ],
  );

  const getFieldOptions = useCallback(
    (field: string | undefined): FormChatListOption[] =>
      field === "bathCategoryId" ? categoryOptions : [],
    [categoryOptions],
  );

  const isListField = useCallback(
    (field: string | undefined) => field === "bathCategoryId",
    [],
  );

  const getFieldInput = useCallback(
    (field: string | undefined): FormChatFieldInput | undefined => {
      if (!field) return undefined;
      if (field === "bathCategoryId") {
        return {
          type: "list_select",
          field,
          searchable: true,
          openListLabel: "Ver categorias",
          changeLabel: "Alterar",
          allowFreeText: false,
          topSuggestions: categoryOptions.slice(0, 4).map((option) => option.label),
        };
      }
      if (field === "bathImage") {
        return { type: "image", field, quickReplies: ["Pular"], allowFreeText: false };
      }
      if (LIST_FIELDS.has(field)) {
        return {
          type: "quick_replies",
          field,
          quickReplies: RITUAL_BATH_LIST_EDIT_REPLIES,
          allowFreeText: true,
        };
      }
      if (field === "bathStatus") {
        return {
          type: "quick_replies",
          field,
          quickReplies: Object.values(STATUS_LABELS),
          allowFreeText: true,
        };
      }
      if (field === "scheduleOnCalendar") {
        return {
          type: "quick_replies",
          field,
          quickReplies: ["Sim", "Não"],
          allowFreeText: true,
        };
      }
      if (field === "scheduleDate") {
        return { type: "date", field, allowFreeText: true };
      }
      if (field === "scheduleStartTime" || field === "scheduleEndTime") {
        return { type: "time", field, quickReplies: ["Pular"], allowFreeText: true };
      }
      if (field === "bathTitle") {
        return { type: "text", field, allowFreeText: true };
      }
      return { type: "text", field, quickReplies: ["Pular"], allowFreeText: true };
    },
    [categoryOptions],
  );

  const resolveFieldInput = useCallback(
    (
      field: string | undefined,
      serverFieldInput: FormChatFieldInput | undefined,
    ): FormChatFieldInput | undefined => {
      if (field === "bathStatus") {
        const serverReplies = serverFieldInput?.quickReplies?.filter(Boolean) ?? [];
        return {
          type: "quick_replies",
          field,
          quickReplies: serverReplies.length > 0 ? serverReplies : Object.values(STATUS_LABELS),
          allowFreeText: true,
        };
      }
      if (field === "scheduleOnCalendar" && serverFieldInput?.type === "text") {
        return getFieldInput(field);
      }
      return serverFieldInput ?? getFieldInput(field);
    },
    [getFieldInput],
  );

  /** Lists are edited through actions, so their prompt names the actions instead of asking for "the new value". */
  const getEditPrompt = useCallback((field: string, label: string) => {
    if (field === "bathIngredients") {
      return "Mande novos ingredientes para adicionar ou escolha uma opção abaixo.";
    }
    if (field === "bathSteps") {
      return "Mande novos passos para adicionar ou escolha uma opção abaixo.";
    }
    if (field === "bathImage") {
      return `Toque em "Adicionar foto" para escolher a nova ${label.toLowerCase()} ou em "Pular" para manter a atual.`;
    }
    return undefined;
  }, []);

  const editableFields = useMemo(
    () =>
      formState.scheduleOnCalendar
        ? EDITABLE_FIELDS
        : EDITABLE_FIELDS.filter((field) => !SCHEDULE_DETAIL_FIELDS.has(field.key)),
    [formState.scheduleOnCalendar],
  );

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const ingredientCount = formState.bathIngredients?.length ?? 0;
    const stepCount = formState.bathSteps?.length ?? 0;
    const optionalText = (value: string | null | undefined) =>
      value?.trim() ? value : "Não informado";
    const currentValues: Record<string, string | undefined> = {
      bathCategoryId: summary?.bathCategoryName ?? formState.bathCategoryName,
      bathTitle: summary?.bathTitle ?? formState.bathTitle,
      bathDescription: optionalText(formState.bathDescription),
      bathIntroduction: optionalText(formState.bathIntroduction),
      bathSource: optionalText(formState.bathSource),
      bathImage:
        image || formState.bathImageUrl || formState.bathImage === "attached"
          ? "Com foto"
          : "Sem foto",
      bathIngredients: countLabel(ingredientCount, "ingrediente", "ingredientes"),
      bathSteps: countLabel(stepCount, "passo", "passos"),
      bathStatus:
        summary?.bathStatusLabel ??
        (formState.bathStatus ? STATUS_LABELS[formState.bathStatus] : undefined),
      scheduleOnCalendar: formState.scheduleOnCalendar ? "Sim" : "Não",
      scheduleDate: formatChatDate(formState.scheduleDate),
      scheduleStartTime: formState.scheduleStartTime ?? "Sem horário",
      scheduleEndTime: formState.scheduleEndTime ?? "Sem horário",
    };
    return editableFields.map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValues[field.key],
    }));
  }, [editableFields, formState, image, summary]);

  const handleConfirmRitualBath = useCallback(async () => {
    if (!summary || !sessionId) {
      onError?.({
        code: "FORM_CHAT_INCOMPLETE",
        message: "Revise os dados do banho no chat antes de confirmar.",
      });
      return;
    }
    let response: FormChatMessageResponse | undefined;
    try {
      response = await sendRitualBathRequest({
        sessionId,
        message: "",
        action: "confirm",
      });
    } catch {
      return;
    }
    if (!response || response.currentStep !== "complete") return;

    try {
      await submitRitualBathByChat({
        state: { ...formState, ...response.formState },
        dirtyFields: response.dirtyFields ?? dirtyFields,
        image,
      });
      setImage(null);
      onReset();
    } catch (err: unknown) {
      onError?.(
        extractAxiosApiError(
          err,
          isEdit
            ? "Não foi possível salvar as alterações. Revise os dados e tente novamente."
            : "Não foi possível cadastrar o banho. Revise os dados e tente novamente.",
        ),
      );
    }
  }, [
    dirtyFields,
    formState,
    image,
    isEdit,
    onError,
    onReset,
    sendRitualBathRequest,
    sessionId,
    submitRitualBathByChat,
    summary,
  ]);

  const transcribeAudio = useCallback(
    async (audio: Blob): Promise<string> => {
      if (terreiroId) {
        return (
          await formChatRepository.transcribeAudio(terreiroId, audio, {
            entityType: "ritual_bath",
            mode: isEdit ? "edit" : "create",
          })
        ).transcript;
      }
      if (mediumId) {
        return (await formChatRepository.transcribePersonalAudio(mediumId, audio))
          .transcript;
      }
      throw new Error("Owner scope required");
    },
    [isEdit, mediumId, terreiroId],
  );

  const isBusy = isPending || isSubmitting;

  const displaySummary = useMemo<RitualBathChatSummary | undefined>(
    () =>
      summary && imagePreviewUrl
        ? { ...summary, bathImageUrl: imagePreviewUrl, hasNewImage: true }
        : summary,
    [imagePreviewUrl, summary],
  );

  const summaryCard = useMemo(() => {
    if (!displaySummary) return null;
    return (
      <RitualBathFormChatSummaryCard
        summary={displaySummary}
        confirmLabel={confirmLabel}
        isLoading={isBusy}
        onConfirm={handleConfirmRitualBath}
        onEdit={onEdit}
      />
    );
  }, [confirmLabel, displaySummary, handleConfirmRitualBath, isBusy, onEdit]);

  return {
    ritualBathReferenceData,
    sendRitualBathRequest,
    handleConfirmRitualBath,
    summaryCard,
    editableFields,
    editableFieldOptions,
    getFieldOptions,
    getFieldInput,
    resolveFieldInput,
    getEditPrompt,
    isListField,
    transcribeAudio,
    attachImage: setImage,
    canCreate,
    isLimitLoading,
    limitMessage,
    isPending: isBusy,
  };
}
